import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { ShiftPositionType } from "@/types/db/shift_positions";
import { PositionStats } from "./types";
import { PositionStatusBar } from "./PositionStatusBar";
import { StatusGroup } from "./StatusGroup";
import { groupUsersByStatus } from "./utils";

type PositionTooltipContentProps = {
  position: ShiftPositionType;
  stats: PositionStats;
};

export function PositionTooltipContent({ position, stats }: PositionTooltipContentProps) {
  const { colors } = useTheme();
  const usersByStatus = groupUsersByStatus(position.shift_users || []);
  const filledCount = stats.accepted + stats.invited + stats.queued + stats.complete;

  return (
    <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {/* Header */}
      <ThemedText style={styles.header}>{position.position?.name || "Position"}</ThemedText>

      {/* Status bar */}
      <PositionStatusBar stats={stats} />

      {/* Summary */}
      <ThemedText style={[styles.summary, { color: colors.textSecondary }]}>
        {filledCount} filled / {stats.total} total
      </ThemedText>

      {/* Divider */}
      <View style={[styles.divider, { backgroundColor: colors.border }]} />

      {/* Users by status */}
      <View style={styles.statusGroups}>
        <StatusGroup key="ACCEPTED" status="ACCEPTED" users={usersByStatus.accepted} />
        <StatusGroup key="INVITED" status="INVITED" users={usersByStatus.invited} />
        <StatusGroup key="QUEUED" status="QUEUED" users={usersByStatus.queued} />
        <StatusGroup key="DECLINED" status="DECLINED" users={usersByStatus.declined} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  tooltip: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 8,
    maxWidth: 280,
    minWidth: 200,
  },
  header: {
    fontSize: 14,
    fontWeight: "700",
  },
  summary: {
    fontSize: 12,
    fontWeight: "500",
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 4,
  },
  statusGroups: {
    gap: 10,
  },
});
